import React, {useState, useEffect,Dispatch, useMemo, useRef, SetStateAction, useCallback} from "react";
import { InputFilter } from "../inputFilter/inputFilter"; 
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import "./filter.css"
import "../style/styleText.css"
import { FilterValue, formatFilterLabel, getCuisineDisplayLabel, principalCuisine } from "../types/types";
import { FiCheck } from "react-icons/fi";
import { startTransition } from 'react';
import { isNoteFilter, NOTE_MAX, NOTE_MIN, NOTE_STEP, useNoteFilter } from "../../utils/utilsNotes";
import { PRICE_MAX, PRICE_MIN, PRICE_STEP, usePriceSlider } from "../../utils/utilsPrice";

const SHOW_ALL_FILTERS = new Set(["Type de restaurant"]);


interface FilterInterface {
    value : string,
    liste: string[], 

    listFilter: (string |[number,number])[],
    setListFilter: Dispatch<React.SetStateAction<(string |[number,number])[]>>,
    searchValue: string,
    setShowAll: Dispatch<React.SetStateAction<number>>,
    setBaseClose?:Dispatch<SetStateAction<boolean>>,
    lockedFilterValues?: Set<string>;
    filterResultCounts?: Map<string, number>;
    setShowCantFilter: Dispatch<React.SetStateAction<boolean>>,
}


const Filter:React.FC<FilterInterface> =  React.memo(({setBaseClose,value,liste,listFilter,setListFilter,searchValue,setShowAll,lockedFilterValues=new Set<string>(),filterResultCounts,setShowCantFilter}) => {
    const [show,setShow] = useState(true)
    const classicListToShow = useMemo(() => {
        if (!liste || liste.length === 0) return [];
        return liste;
    }, [liste]);

    const clickShow = () => {
        if(setBaseClose)setBaseClose(show)
        setShow(!show)
    }

    const clickShowAll = () => {
        if(value === 'Origine de la cuisine') startTransition(() => {setShowAll(1)});
        else if(value === 'Type de restaurant') startTransition(() => {setShowAll(2)});
    }

    const orderedCuisineList = useMemo(() => {
        if (value !== "Origine de la cuisine") return liste;
        const filtered = principalCuisine.filter((elem) => liste.includes(elem));
        const remaining = liste.filter((elem) => !principalCuisine.includes(elem));
        return [...filtered, ...remaining];
    }, [liste,value]);
    const visibleCuisineList = useMemo(() => {
        if (orderedCuisineList.length <= 10) return orderedCuisineList;
        return orderedCuisineList.slice(0, 7);
    }, [orderedCuisineList]);

    const fold = (value: unknown) => {
        if (value == null) return "";
        const s = typeof value === "string" ? value : String(value);
        const lower = s.toLowerCase();
        return /[^\x00-\x7F]/.test(lower)
            ? lower
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .replace(/œ/g, "oe")
                .replace(/æ/g, "ae")
                .replace(/ß/g, "ss")
            : lower;
    };

    const listSearch =
        value === "Origine de la cuisine"
            ? (orderedCuisineList ?? [])
            : (liste ?? []);

    const listFolded = React.useMemo(
        () => listSearch.map(raw => ({ raw, norm: fold(raw) })),
        [listSearch]
    );
    const needle = React.useMemo(
        () => fold((searchValue || "").trim()),
        [searchValue]
    );
    const shown = React.useMemo(() => {
        if (!needle) return [];
        return listFolded
            .filter(it => it.norm.includes(needle))
            .map(it => it.raw);
    }, [needle, listFolded]);


    const isFilterSelect = value.startsWith('Mes filtres')
    const isLockedValue = (elem: string) => {
        const isSelected = listFilter.some((filter) => {
            return typeof filter === "string" && filter === elem;
        });

        return lockedFilterValues.has(elem) && !isSelected;
    };
    const orderedTypeRestoList = useMemo(() => {
        if (value !== "Type de restaurant") return liste;
        return [...liste].sort(
            (a, b) =>
                (filterResultCounts?.get(b) ?? 0) -
                (filterResultCounts?.get(a) ?? 0)
        );
    }, [liste, value, filterResultCounts]);
    const applyNoteFilters = useCallback((_previousValues: FilterValue[],selectedValues: FilterValue[]) => {
        startTransition(() => {
            setListFilter(previous => [
                ...previous.filter(
                    filter => !isNoteFilter(filter)
                ),
                ...selectedValues
            ]);
        });
    },[setListFilter]);
    const currentPrice = useMemo(() => {
        return listFilter.find(
            (filter): filter is [number, number] =>
                Array.isArray(filter) &&
                filter.length === 2 &&
                filter.every(value => typeof value === "number")
        );
    }, [listFilter]);
    const applyPrice = useCallback(
        (min: number, max: number) => {
            startTransition(() => {
                setListFilter(previous => {
                    const withoutPrice = previous.filter(
                        filter =>
                            !(Array.isArray(filter) &&filter.length === 2 &&filter.every(value => typeof value === "number"))
                    );
                    if (min === PRICE_MIN &&max === PRICE_MAX) return withoutPrice;

                    return [
                        ...withoutPrice,
                        [min, max] as [number, number]
                    ];
                });
            });
    }, [setListFilter]);
    const {minValue,maxValue,middle,showCheck,priceWrapperRef,handleMinChange,handleMaxChange,handlePriceClick,handlePriceTouchStart,handlePriceTouchMove,handlePriceTouchEnd} = usePriceSlider({initialMin: currentPrice?.[0] ?? PRICE_MIN,initialMax: currentPrice?.[1] ?? PRICE_MAX,onPriceChange: applyPrice})
    const {noteValue,noteLabel,notePercentage,noteVisualPercentage,noteLabelPercentage,showCheckNote,noteWrapperRef,handleNoteChange,handleNoteClick,handleNoteTouchStart,handleNoteTouchMove,handleNoteTouchEnd} = useNoteFilter({listFilter,applyNoteFilters});

    return (
        <div className='containerListFilter'>
        { searchValue ? 
            shown && shown.length>0? <div className='listElem'> 
                {
                    shown.map((elem, index) => {
                    const isLocked = isLockedValue(elem);
                    return <div key={`${value}-search-${elem}`} className={`elem ${isLocked ? "isLocked" : ""}`}><InputFilter setShowCantFilter={setShowCantFilter} value={elem} displayValue={getCuisineDisplayLabel(formatFilterLabel(elem))} show={true} id={elem+index.toString()} listFilter={listFilter} setListFilter={setListFilter} isLocked={isLocked}/></div>
                    }) 
                }
            </div> : <></>
        :
        <>
            <div className="titleSection pointer" onClick={clickShow}>
                <p className='title midTextBold'>{value}</p>    
                {show?
                            <FiChevronUp className="Chevron" size={20}  />
                        :  <FiChevronDown className="Chevron" size={20} />
                }      
            </div>
            <div className={`listElem ${show ? 'isShow' : ''}`}>            
                {
                    show && <> {
                        value === 'Origine de la cuisine' ? (
                        
                            visibleCuisineList.map((elem, index) => {
                                const isLocked = isLockedValue(elem);
                                return <div key={`${value}-${elem}`} className={`elem ${isLocked ? "isLocked" : ""}`}>
                                    <InputFilter
                                        setShowCantFilter={setShowCantFilter}
                                        value={elem}
                                        show={show}
                                        isCuisine={true}
                                        id={elem + index.toString()}
                                        listFilter={listFilter}
                                        setListFilter={setListFilter}
                                        isLocked={isLocked}
                                    />
                                </div>
                        }))
                        : value === 'Type de restaurant' ? 
                                orderedTypeRestoList && orderedTypeRestoList.length>0 && orderedTypeRestoList.slice(0,7).map((elem, index) => {
                                    const isLocked = isLockedValue(elem);
                                    return <div key={`${value}-${elem}`} className={`elem ${isLocked ? "isLocked" : ""}`}><InputFilter setShowCantFilter={setShowCantFilter} value={elem} displayValue={formatFilterLabel(elem)} show={show} id={elem+index.toString()} listFilter={listFilter} setListFilter={setListFilter} isLocked={isLocked}/></div>
                                }) 
                                :
                                classicListToShow.map((elem, index) => {
                                    const isLocked = isLockedValue(elem);
                                    return <div key={`${value}-${elem}`} className={`elem ${(isLocked && !value.startsWith('Mes filtres')) ? 'isLocked' : ''}`}><InputFilter setShowCantFilter={setShowCantFilter} value={elem} displayValue={value.startsWith('Mes filtres') ? (isFilterSelect ? getCuisineDisplayLabel(formatFilterLabel(elem)) : formatFilterLabel(elem)) : undefined} show={show} id={elem+index.toString()} listFilter={listFilter} setListFilter={setListFilter} isLocked={isLocked}/></div>
                                }) 
                    }
                    </>
                          
                }
                {isFilterSelect && show && classicListToShow.length >= 1 && <div className="elem deleteFilter">
                    <div className={`content pointer`} onClick={() => setListFilter([])}>
                        <span className={`XsmallText deleteFilter`} >Tout effacer</span>        
                    </div>
                </div>}  
            </div>
            {
                show && <>{
                    value === 'Prix par personne' ? (
                            <div className="slideFilter slidePrice slideDobble XsmallText" onTouchStart={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}> 
                                <div className="value flexAllCenter" style={{marginLeft:`${minValue < 30 ? minValue+3 : minValue < 50 ? minValue+((50-minValue)/10) : minValue+((50-minValue)/10)+1}%`}}>{minValue+'€'}</div>
                                <div className="pointerValue" style={{marginLeft:`${minValue < 30 ? minValue+3 : minValue < 50 ? minValue+((50-minValue)/10) : minValue+((50-minValue)/10)+1}%`}}></div>
                                <div className="value flexAllCenter" style={{marginLeft:`${maxValue < 40 ?  maxValue + 2 : maxValue < 50 ?  maxValue + 1 : maxValue <= 90 ? maxValue : maxValue-2}%`}}>{maxValue === 100 ? '100€+' : maxValue+'€'}</div>
                                <div className="pointerValue" style={{marginLeft:`${maxValue < 40 ?  maxValue + 2 :  maxValue < 50 ?  maxValue + 1 : maxValue <= 90 ? maxValue : maxValue-2}%`}}></div>
                                
                                <div className="patchClose" onTouchStart={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()}></div>
                                <div className="range-wrapper" ref={priceWrapperRef}>
                                    <div className="price-touch-zone price-touch-zone-min" style={{ left: 0, width: `${middle}%` }} onClick={(e) => handlePriceClick(e, "min")} onTouchStart={handlePriceTouchStart} onTouchMove={(e) => handlePriceTouchMove(e, "min")} onTouchEnd={handlePriceTouchEnd}/>
                                    <div className="price-touch-zone price-touch-zone-max" style={{ left: `${middle}%`, width: `${100 - middle}%` }} onClick={(e) => handlePriceClick(e, "max")} onTouchStart={handlePriceTouchStart} onTouchMove={(e) => handlePriceTouchMove(e, "max")} onTouchEnd={handlePriceTouchEnd} />
                                    <input type="range" aria-label="Prix minimum en euros" id="minRange" min="0" max="100" step={PRICE_STEP} value={minValue} onChange={(e) => handleMinChange(e)}/>
                                    <input type="range" aria-label="Prix maximum en euros" id="maxRange" min="0" max="100" step={PRICE_STEP} value={maxValue} onChange={(e) => handleMaxChange(e)}/>
                                </div>
                                
                                <div className="slider-track" style={{left: `${Math.min(minValue, 88)}%`,width: `${Math.min(99, Math.max(11, maxValue - minValue))}%`}}></div>
                                {showCheck && <span className="checkAnimation"><FiCheck size={20} color="black" /></span>}
                                <div className="indicationSlide"><span>0 €</span><span>100 € +</span></div>
                            </div> 
                            )
                            : value === 'Notes' ? (
                            <div className="slideFilter slidePrice slideDobble XsmallText" onTouchStart={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}> 
                                <div className="value flexAllCenter" style={{marginLeft:`${noteLabelPercentage}%`}}>{noteLabel}</div>
                                <div className="pointerValue" style={{marginLeft:`${noteVisualPercentage}%`}}></div>  

                                <div className="patchClose" onTouchStart={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()}></div>
                                <div className="range-wrapper" ref={noteWrapperRef}>
                                    <div className="price-touch-zone " style={{ left: 0, width: `100%` }} onClick={handleNoteClick} onTouchStart={handleNoteTouchStart} onTouchMove={handleNoteTouchMove} onTouchEnd={handleNoteTouchEnd} />
                                    <input type="range" aria-label="Note des restaurants" id="notes" min={NOTE_MIN} max={NOTE_MAX} step={NOTE_STEP} value={noteValue} onChange={handleNoteChange}/>
                                </div>

                                <div className="slider-track sliderNote" style={{right: '-2px',width: `${100 - notePercentage}%`}}></div>
                                {showCheckNote && <span className="checkAnimation"><FiCheck size={20} color="black" /></span>}
                                <div className="indicationSlide indicationNote"><span>Toutes les notes</span><span>5 étoiles</span></div>
                            </div> 
                            )
                            :
                            value === 'Origine de la cuisine' ? (
                                orderedCuisineList.length >= 11 ? (
                                    <p className="XsmallText showAllListFilter lowOpacityHover pointer" onClick={clickShowAll}>Tout afficher</p>
                                ) : undefined
                            ) : SHOW_ALL_FILTERS.has(value) && orderedTypeRestoList.length > 7  ?  
                                <p className={`XsmallText showAllListFilter lowOpacityHover pointer`} onClick={clickShowAll}>Tout afficher</p> 
                                : undefined
                    }</>
            }
            </>}

            
            </div>
    )
});

export {Filter}